/**
 * Fixes YouTube iframes in migrated HTML content to prevent 513 errors.
 *
 * Issues addressed:
 * - Adds referrerpolicy="strict-origin-when-cross-origin" attribute
 * - Converts protocol-relative URLs (//) to https://
 * - Adds loading="lazy" so the player is not fetched during initial page load
 *
 * These are raw iframes baked into migrated HTML, so they cannot be replaced by
 * the click-to-play `Video` component (which every other video render path uses).
 * Deferring the load is the closest equivalent available here.
 *
 * @param html - The HTML string containing potential YouTube iframes
 * @returns The HTML with fixed YouTube iframes
 */
export function fixYoutubeIframes(html: string): string {
  if (!html) { return html; }

  // Match YouTube iframes (youtube.com or youtube-nocookie.com)
  const iframeRegex = /<iframe([^>]*)(src=["'](?:https?:)?\/\/(?:www\.)?(?:youtube\.com|youtube-nocookie\.com)\/embed\/[^"']+["'])([^>]*)>/gi;

  return html.replace(iframeRegex, (match, _beforeSrc, srcAttr, _afterSrc) => {
    let fixedIframe = match;

    // Fix protocol-relative URLs (// -> https://)
    if (srcAttr.includes('src="//') || srcAttr.includes("src='//")) {
      fixedIframe = fixedIframe.replace(/src=["']\/\//g, 'src="https://');
    }

    // Add referrerpolicy if not present
    if (!fixedIframe.includes('referrerpolicy')) {
      fixedIframe = fixedIframe.replace(/<iframe/, '<iframe referrerpolicy="strict-origin-when-cross-origin"');
    }

    // Defer the embed until it is near the viewport
    if (!/\bloading\s*=/.test(fixedIframe)) {
      fixedIframe = fixedIframe.replace(/<iframe/, '<iframe loading="lazy"');
    }

    return fixedIframe;
  });
}
